import { useEffect, useState } from "react";

import { ConfirmModal } from "@common";
import { nonBankingReason } from "@utils/ontarioBankingDays";

// "2026-08-15" → "Sat, Aug 15". Parsed field-by-field, never through Date(iso),
// which reads the string as UTC and can land the label a day off.
function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function parseISO(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function fmtMoney(value) {
  if (value == null) return "";
  return `$${Math.round(Math.abs(Number(value))).toLocaleString("en-CA")}`;
}

// Money for an <input>: plain digits and at most one decimal point, so what is
// typed round-trips through Number(). Mirrors MoveOccurrenceModal, which edits
// the same amounts through the same exception field.
const toAmountInput = (value) =>
  value == null || value === "" ? "" : String(Math.abs(Number(value)));

// Compare in whole cents — the stored value is a 2-decimal DecimalField, so
// comparing floats would report a change for a value the API cannot even hold.
const cents = (value) => Math.round(Number(value) * 100);

// Passed where a handler is required but must do nothing; a new arrow here
// would give the modal a fresh prop identity on every render.
const NOOP = () => {};

const DATE_INPUT_CLS =
  "w-full rounded-lg border bg-white px-2.5 py-1.5 text-sm text-palette-gray-primary transition-colors focus:outline-none focus:ring-2";

/**
 * Confirm — and adjust — moving scheduled payments off a day the bank is closed.
 *
 * The grid computes these dates from each schedule's recurring rule, so some
 * land on a weekend or an Ontario bank holiday. Accepting writes a one-off
 * exception per payment: a real change to when money leaves the account, so
 * every row states the payment, what it costs, the day it cannot clear and WHY.
 *
 * The next banking day is a PROPOSAL, not a verdict. Which day a payment
 * actually moves to is a business call — a landlord may take the Friday before,
 * a payroll run may have to wait for the Tuesday — so every row's target date is
 * editable, and the batch saves whatever the rows end up holding.
 *
 * A row can also be dropped from the batch outright, for the payment that is
 * meant to sit where it is. Dropping is shown, not vanished: the row stays
 * visible and dimmed with its own undo, because a list that silently shrinks as
 * you click through it is one you cannot check before agreeing to it.
 *
 * Declining is a first-class answer, not a delay: the payments stay where the
 * rule put them and the prompt does not return for the rest of the session.
 *
 * The shared ConfirmModal supplies the shell — the panel, the escape/backdrop
 * close, the footer buttons, and the scrolling body a long list needs. Only the
 * list itself is local, because only it is specific to this decision.
 */
export default function ConfirmNonBankingMovesModal({
  isOpen,
  moves = [],
  onConfirm,
  onClose,
  saving = false,
}) {
  const count = moves.length;

  // { [move.key]: "YYYY-MM-DD" } — the date each row will actually be moved to,
  // seeded with the proposal. Keyed by move, not index, so a re-proposal that
  // adds or drops a payment cannot shift one row's edit onto another.
  const [dates, setDates] = useState({});
  // { [move.key]: "1234.56" } — the raw input string, not a number, so a
  // half-typed "12." is not rewritten under the cursor.
  const [amounts, setAmounts] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    // Seeded on open and whenever the proposal itself changes. Existing edits
    // are carried over by key, so a refetch behind the modal cannot silently
    // reset a date or an amount the user already set.
    setDates((prev) => {
      const next = {};
      moves.forEach((move) => {
        next[move.key] = prev[move.key] ?? move.newDate;
      });
      return next;
    });
    setAmounts((prev) => {
      const next = {};
      moves.forEach((move) => {
        next[move.key] = prev[move.key] ?? toAmountInput(move.amount);
      });
      return next;
    });
  }, [isOpen, moves]);

  // Keys the user has dropped from the batch. Held here rather than by filtering
  // `moves`, so the row can stay on screen and the drop stays undoable.
  const [removed, setRemoved] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) return;
    // Drops are pruned to the current proposal for the same reason the dates are
    // kept: a key that is no longer offered must not linger and silently drop a
    // future payment that happens to reuse it.
    setRemoved((prev) => {
      const keys = new Set(moves.map((move) => move.key));
      const next = new Set([...prev].filter((key) => keys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [isOpen, moves]);

  const dateFor = (move) => dates[move.key] ?? move.newDate;
  const setDate = (move, value) => setDates((prev) => ({ ...prev, [move.key]: value }));
  const amountFor = (move) => amounts[move.key] ?? toAmountInput(move.amount);
  const setAmount = (move, value) => setAmounts((prev) => ({ ...prev, [move.key]: value }));

  // Why an amount cannot be saved, or null when it can. Blank counts: a payment
  // with no amount is not a payment.
  const amountError = (move) => {
    const raw = amountFor(move);
    if (raw === "" || raw == null) return "Enter an amount.";
    const n = Number(raw);
    if (!Number.isFinite(n)) return "Not a number.";
    if (n <= 0) return "Must be over zero.";
    return null;
  };
  const isRemoved = (move) => removed.has(move.key);
  const toggleRemoved = (move) =>
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(move.key)) next.delete(move.key);
      else next.add(move.key);
      return next;
    });

  // Only the rows still in the batch are written — and only they are counted, so
  // the button never promises more moves than it makes.
  const kept = moves.filter((move) => !isRemoved(move));

  // A row is only BLOCKING when it has no date at all. A date that is itself a
  // weekend or a holiday is warned about, not refused: moving a payment onto a
  // closed day is unusual but it is the user's call to make, and the same
  // warn-don't-block rule already governs the schedule modal.
  const invalid = kept.filter((move) => !dateFor(move) || amountError(move));

  function handleConfirm() {
    if (invalid.length || !kept.length) return;
    onConfirm?.(
      kept.map((move) => {
        const amount = amountFor(move);
        // `newAmount` is undefined when the amount was left alone, so the caller
        // can omit it from the payload and leave the schedule's own amount in
        // place rather than pinning an override that says the same thing.
        const changed = amount !== "" && cents(amount) !== cents(move.amount);
        return {
          ...move,
          newDate: dateFor(move),
          ...(changed ? { newAmount: Number(amount) } : {}),
        };
      }),
    );
  }

  return (
    <ConfirmModal
      isOpen={isOpen}
      // Escape and the backdrop are inert mid-save. The footer's Cancel is
      // already disabled by `isLoading`, and a dismissal that only hides the
      // modal while the request is still in flight would misreport it as
      // declined.
      onClose={saving ? NOOP : onClose}
      onConfirm={handleConfirm}
      isLoading={saving}
      confirmDisabled={!kept.length || invalid.length > 0}
      // Wide enough that a payment name, both its dates, an editable amount and
      // the drop control sit on ONE line: this is a list to scan and adjust
      // before agreeing to it, and a name wrapping onto three lines is what
      // makes a dozen rows unreadable.
      size="xl"
      title="Move payments off a closed day?"
      confirmVariant="primary"
      confirmText={kept.length === 1 ? "Move payment" : `Move ${kept.length} payments`}
      cancelText="Not now"
      message={
        <div>
          <p className="text-sm text-palette-gray-secondary">
            {count} scheduled {count === 1 ? "payment lands" : "payments land"} on a day the bank
            does not move money. The next banking day is filled in for each — change any of them, or
            drop the ones that should stay put. Only the payments left in the list change; each
            recurring schedule keeps its own dates.
          </p>

          <div className="mt-4 overflow-hidden rounded-xl border border-palette-gray-divider">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-palette-gray-subtle text-left text-[11px] uppercase tracking-wider text-palette-gray-secondary">
                  <th className="px-4 py-2 font-semibold">Payment</th>
                  <th className="px-4 py-2 font-semibold">Scheduled</th>
                  <th className="w-48 px-4 py-2 font-semibold">Moves to</th>
                  <th className="w-36 px-4 py-2 text-right font-semibold">Amount</th>
                  <th className="w-10 px-2 py-2">
                    <span className="sr-only">Drop from batch</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-palette-gray-divider">
                {moves.map((move) => {
                  const dropped = isRemoved(move);
                  const value = dateFor(move);
                  // Recomputed from what the row now HOLDS, not from the
                  // proposal: picking another closed day has to say so.
                  const closed = value ? nonBankingReason(parseISO(value)) : null;
                  const amtError = dropped ? null : amountError(move);
                  return (
                    <tr
                      key={move.key}
                      className={`align-top ${dropped ? "bg-palette-gray-subtle/60 text-palette-gray-placeholder" : ""}`}
                    >
                      <td
                        className={`px-4 py-2.5 font-medium ${dropped ? "line-through" : "text-palette-gray-heading"}`}
                      >
                        {move.label}
                      </td>
                      {/* The REASON rides with the date it explains — "a Saturday",
                          "Canada Day" — so the move is checkable, not just asserted. */}
                      <td className={`px-4 py-2.5 ${dropped ? "" : "text-palette-gray-secondary"}`}>
                        {fmtDate(move.currentDate)}
                        {move.reason ? (
                          <span className="text-palette-gray-placeholder"> · {move.reason}</span>
                        ) : null}
                      </td>

                      {/* A dropped row keeps its shape but loses its inputs: there
                          is nothing to edit about a payment that is not moving,
                          and a live field would invite an edit that goes nowhere. */}
                      <td className="px-4 py-2">
                        {dropped ? (
                          <span className="inline-block py-1.5">Staying put</span>
                        ) : (
                          <>
                            <input
                              type="date"
                              value={value ?? ""}
                              disabled={saving}
                              onChange={(event) => setDate(move, event.target.value)}
                              aria-label={`Move ${move.label} from ${move.currentDate} to`}
                              className={`${DATE_INPUT_CLS} ${
                                !value
                                  ? "border-palette-red-border focus:border-palette-red-border focus:ring-palette-red-border"
                                  : closed
                                    ? "border-palette-amber-border focus:border-palette-amber-border focus:ring-palette-amber-border"
                                    : "border-palette-gray-divider focus:border-palette-indigo-ring focus:ring-palette-indigo-border"
                              }`}
                            />
                            {!value ? (
                              <p className="mt-1 text-xs text-palette-red-text">Pick a date.</p>
                            ) : closed ? (
                              <p className="mt-1 text-xs text-palette-amber-primary">
                                Also closed — {closed}.
                              </p>
                            ) : null}
                          </>
                        )}
                      </td>

                      <td className="px-4 py-2 text-right">
                        {dropped ? (
                          <span className="inline-block py-1.5 tabular-nums line-through">
                            {fmtMoney(move.amount)}
                          </span>
                        ) : (
                          <>
                            <div className="relative">
                              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-palette-gray-placeholder">
                                $
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={amountFor(move)}
                                disabled={saving}
                                onChange={(event) => setAmount(move, event.target.value)}
                                aria-label={`Amount for ${move.label}`}
                                className={`${DATE_INPUT_CLS} pl-6 text-right tabular-nums ${
                                  amtError
                                    ? "border-palette-red-border focus:border-palette-red-border focus:ring-palette-red-border"
                                    : "border-palette-gray-divider focus:border-palette-indigo-ring focus:ring-palette-indigo-border"
                                }`}
                              />
                            </div>
                            {amtError ? (
                              <p className="mt-1 text-right text-xs text-palette-red-text">
                                {amtError}
                              </p>
                            ) : null}
                          </>
                        )}
                      </td>

                      <td className="px-2 py-2.5">
                        <button
                          type="button"
                          onClick={() => toggleRemoved(move)}
                          disabled={saving}
                          title={dropped ? "Put back in the batch" : "Leave this payment alone"}
                          aria-label={
                            dropped
                              ? `Put ${move.label} back in the batch`
                              : `Leave ${move.label} where it is`
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-palette-gray-divider text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle disabled:opacity-50"
                        >
                          {dropped ? (
                            /* undo */
                            <svg
                              className="h-3.5 w-3.5"
                              viewBox="0 0 16 16"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.6"
                            >
                              <path
                                d="M3 8a5 5 0 1 1 1.6 3.7M3 4.5V8h3.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="h-3.5 w-3.5"
                              viewBox="0 0 16 16"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.6"
                            >
                              <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                            </svg>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Said plainly rather than left to be inferred from a struck-through
              row: with nothing left in the batch, Move has nothing to do. */}
          {!kept.length && (
            <p className="mt-3 text-sm text-palette-amber-primary">
              Every payment is set to stay put — nothing to move. Close with “Not now”, or put one
              back.
            </p>
          )}
        </div>
      }
    />
  );
}
